/**
 * P8 — Public Registration page (Hybrid Elite premium black + gold).
 * Route: /register/entity/:token?
 *
 * Flow: token verify (optional) → OTP request → OTP verify → multi-step form
 * (Identity → Category → Services & Products → Intent → Review).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, User, Globe, Briefcase, Sparkles, Loader2, Mail, ShieldCheck,
  ChevronRight, ChevronLeft, Plus, X, CheckCircle2, AlertTriangle, ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITY_OPTIONS = [
  { id: 'company',      label: 'Company',      icon: Building2 },
  { id: 'individual',   label: 'Individual',   icon: User },
  { id: 'organization', label: 'Organization', icon: Globe },
];

const ROLE_OPTIONS = [
  { id: 'client',    label: 'Client',          tag: 'Looking for our products/services' },
  { id: 'supplier',  label: 'Supplier',        tag: 'Provide products or services' },
  { id: 'partner',   label: 'Partner',         tag: 'Strategic collaboration' },
  { id: 'investor',  label: 'Investor',        tag: 'Capital or financing' },
  { id: 'other',     label: 'Other',           tag: 'Tell us more' },
];

const TOTAL_STEPS = 6; // 0=email/otp, 1=identity, 2=role, 3=services/products, 4=intent, 5=review

const EntityRegistrationPage = () => {
  const { token } = useParams();
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(!!token);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [error, setError] = useState(null);

  // OTP state
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Identity
  const [entityType, setEntityType] = useState('company');
  const [legalName, setLegalName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [website, setWebsite] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [legalRep, setLegalRep] = useState('');

  // Role
  const [role, setRole] = useState('');
  const [roleOther, setRoleOther] = useState('');

  // Services / Products
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [productCatalog, setProductCatalog] = useState([]);
  const [services, setServices] = useState([]); // [{name, is_new, description}]
  const [products, setProducts] = useState([]);

  // Intent
  const [partnershipType, setPartnershipType] = useState('');
  const [objective, setObjective] = useState('');
  const [intentDesc, setIntentDesc] = useState('');

  // -------- Token verification on mount
  useEffect(() => {
    if (!token) { setTokenLoading(false); return; }
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/onboarding/public/token/${token}`);
        setTokenInfo(r.data);
        if (r.data?.valid) {
          if (r.data.target_email) setOtpEmail(r.data.target_email);
          if (r.data.suggested_role) setRole(r.data.suggested_role);
        }
      } catch {
        setTokenInfo({ valid: false, reason: 'Could not verify the link.' });
      } finally {
        setTokenLoading(false);
      }
    })();
  }, [token]);

  // -------- Pull taxonomy when entering services/products step
  useEffect(() => {
    if (step !== 3) return;
    (async () => {
      try {
        const [s, p] = await Promise.all([
          axios.get(`${API_URL}/api/onboarding/public/taxonomy?kind=service&limit=80`),
          axios.get(`${API_URL}/api/onboarding/public/taxonomy?kind=product&limit=80`),
        ]);
        setServiceCatalog(s.data || []);
        setProductCatalog(p.data || []);
      } catch { /* graceful */ }
    })();
  }, [step]);

  // -------- OTP handlers
  const requestOtp = useCallback(async () => {
    if (!otpEmail) { setError('Email is required.'); return; }
    setError(null); setOtpLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/onboarding/public/otp/request`, {
        email: otpEmail, purpose: 'onboarding',
      });
      if (r.data?.sent === false) {
        setError(r.data.reason || 'Could not send code. Please try again.');
        return;
      }
      setOtpRequested(true);
      setContactEmail((prev) => prev || otpEmail);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send code.');
    } finally { setOtpLoading(false); }
  }, [otpEmail]);

  const verifyOtp = useCallback(async () => {
    if (!otpCode || otpCode.length !== 6) { setError('Enter the 6-digit code.'); return; }
    setError(null); setOtpLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/onboarding/public/otp/verify`, {
        email: otpEmail, code: otpCode, purpose: 'onboarding',
      });
      if (r.data.valid) {
        setOtpVerified(true);
        setStep(1);
      } else {
        setError(r.data.reason || 'Invalid code.');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Verification failed.');
    } finally { setOtpLoading(false); }
  }, [otpEmail, otpCode]);

  // -------- Service / Product chip helpers
  const addService = (name, isNew = false, description = '') => {
    if (!name.trim()) return;
    if (services.some(s => s.name.toLowerCase() === name.toLowerCase())) return;
    setServices([...services, { name: name.trim(), is_new: isNew, description }]);
  };
  const addProduct = (name, isNew = false, description = '') => {
    if (!name.trim()) return;
    if (products.some(p => p.name.toLowerCase() === name.toLowerCase())) return;
    setProducts([...products, { name: name.trim(), is_new: isNew, description }]);
  };

  // -------- Final submit
  const submit = useCallback(async () => {
    setError(null); setSubmitting(true);
    try {
      const payload = {
        token: token || undefined,
        otp_email: otpEmail, otp_code: otpCode,
        entity_type: entityType,
        legal_name: legalName.trim(),
        tax_id: taxId || null, country: country || null,
        address: address || null, website: website || null,
        contact_name: contactName.trim(),
        contact_email: contactEmail.toLowerCase().trim(),
        contact_phone: contactPhone || null,
        legal_representative: legalRep || null,
        role, role_other: role === 'other' ? roleOther : null,
        services, products,
        intent: (partnershipType || objective || intentDesc) ? {
          partnership_type: partnershipType || null,
          objective: objective || null,
          description: intentDesc || null,
        } : null,
        form_language: 'en',
      };
      const r = await axios.post(`${API_URL}/api/onboarding/public/submit`, payload);
      setSubmissionResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Submission failed.');
    } finally { setSubmitting(false); }
  }, [token, otpEmail, otpCode, entityType, legalName, taxId, country, address, website,
      contactName, contactEmail, contactPhone, legalRep, role, roleOther,
      services, products, partnershipType, objective, intentDesc]);

  const canAdvance = useMemo(() => {
    if (step === 1) return !!legalName && !!contactName && !!contactEmail;
    if (step === 2) return !!role && (role !== 'other' || !!roleOther);
    if (step === 3) return services.length > 0 || products.length > 0;
    return true;
  }, [step, legalName, contactName, contactEmail, role, roleOther, services, products]);

  // ============================================================ Render
  if (tokenLoading) {
    return (
      <Shell>
        <div className="text-[#C9A86A] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Verifying your invitation…</div>
      </Shell>
    );
  }

  if (token && tokenInfo && !tokenInfo.valid) {
    return (
      <Shell>
        <div className="max-w-md w-full p-8 rounded-2xl border border-rose-500/40 bg-rose-950/30">
          <AlertTriangle className="w-10 h-10 text-rose-400 mb-3" />
          <h1 className="text-2xl font-serif text-rose-200 mb-2">Invitation unavailable</h1>
          <p className="text-rose-200/70 text-sm">{tokenInfo.reason}</p>
          <a href="/register/entity" className="inline-block mt-5 text-[#C9A86A] underline text-sm" data-testid="register-direct-signup-link">
            Continue with a direct sign-up
          </a>
        </div>
      </Shell>
    );
  }

  if (submissionResult) {
    return (
      <Shell>
        <div className="max-w-lg w-full p-8 rounded-2xl border border-[#C9A86A]/40 bg-black/40 backdrop-blur" data-testid="register-success-card">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mb-4" />
          <h1 className="text-3xl font-serif text-white mb-3">Submission received</h1>
          <p className="text-white/70 text-sm mb-4">
            Thank you, {submissionResult.contact_name}. Your reference is{' '}
            <span className="font-mono font-bold text-[#C9A86A]">{submissionResult.submission_number}</span>.
          </p>
          <p className="text-white/60 text-xs mb-3">
            Our team will review your registration and respond within 2 business days.
            You will receive updates at <b>{submissionResult.contact_email}</b>.
          </p>
          {submissionResult.ai_executive_brief && (
            <div className="mt-5 p-4 rounded-lg bg-[#C9A86A]/10 border border-[#C9A86A]/20">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#C9A86A] mb-1 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> AI executive brief
              </div>
              <p className="text-white/85 text-xs leading-relaxed">{submissionResult.ai_executive_brief}</p>
            </div>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="max-w-3xl w-full" data-testid="register-page">
        <Header step={step} />

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-rose-900/40 border border-rose-500/40 text-rose-200 text-sm" data-testid="register-error">
            {error}
          </div>
        )}

        {step === 0 && (
          <Card>
            <h2 className="text-2xl font-serif text-white mb-1">Verify your email</h2>
            <p className="text-white/60 text-sm mb-6">
              We will send a one-time 6-digit code to your email to verify it before continuing.
            </p>
            <Field label="Email address">
              <input
                type="email" value={otpEmail}
                onChange={(e) => setOtpEmail(e.target.value)}
                disabled={otpRequested}
                placeholder="you@company.com"
                data-testid="register-otp-email-input"
                className="w-full bg-black/30 border border-white/15 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#C9A86A]"
              />
            </Field>

            {!otpRequested ? (
              <PrimaryButton onClick={requestOtp} loading={otpLoading} testId="register-otp-send-btn">
                <Mail className="w-4 h-4 mr-2" /> Send verification code
              </PrimaryButton>
            ) : (
              <>
                <div className="mb-4 p-3 rounded-lg bg-emerald-900/20 border border-emerald-500/30 text-emerald-200 text-xs flex items-start gap-2" data-testid="register-otp-sent-hint">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Code sent to <span className="font-mono">{otpEmail}</span></div>
                    <div className="text-emerald-300/80 mt-0.5">
                      It usually arrives within a minute. If you do not see it, please check your <b>spam / junk</b> folder.
                    </div>
                  </div>
                </div>
                <Field label="Enter the 6-digit code">
                  <input
                    type="text" inputMode="numeric" maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                    data-testid="register-otp-code-input"
                    className="w-full bg-black/30 border border-white/15 text-white px-4 py-3 rounded-lg text-center font-mono text-2xl tracking-[0.6em] focus:outline-none focus:border-[#C9A86A]"
                  />
                </Field>
                <PrimaryButton onClick={verifyOtp} loading={otpLoading} disabled={otpCode.length !== 6} testId="register-otp-verify-btn">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Verify and continue
                </PrimaryButton>
                <button onClick={() => { setOtpRequested(false); setOtpCode(''); }}
                  className="block mt-3 text-xs text-[#C9A86A]/80 hover:text-[#C9A86A] underline">
                  Resend code
                </button>
              </>
            )}
          </Card>
        )}

        {step === 1 && (
          <Card>
            <h2 className="text-2xl font-serif text-white mb-5">Tell us about your entity</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
              {ENTITY_OPTIONS.map((opt) => (
                <ChoiceCard key={opt.id} active={entityType === opt.id} onClick={() => setEntityType(opt.id)}
                  testId={`register-entity-type-${opt.id}`}>
                  <opt.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{opt.label}</span>
                </ChoiceCard>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Legal name *"><Input value={legalName} onChange={setLegalName} testId="register-legal-name" /></Field>
              <Field label="Tax ID / TRN / VAT"><Input value={taxId} onChange={setTaxId} testId="register-tax-id" /></Field>
              <Field label="Country"><Input value={country} onChange={setCountry} testId="register-country" /></Field>
              <Field label="Website"><Input value={website} onChange={setWebsite} testId="register-website" placeholder="https://" /></Field>
              <Field label="Address" full><Input value={address} onChange={setAddress} testId="register-address" /></Field>
              <Field label="Primary contact name *"><Input value={contactName} onChange={setContactName} testId="register-contact-name" /></Field>
              <Field label="Primary contact email *"><Input value={contactEmail} onChange={setContactEmail} testId="register-contact-email" /></Field>
              <Field label="Primary contact phone"><Input value={contactPhone} onChange={setContactPhone} testId="register-contact-phone" /></Field>
              <Field label="Legal representative"><Input value={legalRep} onChange={setLegalRep} testId="register-legal-rep" /></Field>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <h2 className="text-2xl font-serif text-white mb-2">Your interest</h2>
            <p className="text-white/60 text-sm mb-5">How would you like to engage with Grupo BMP?</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {ROLE_OPTIONS.map((r) => (
                <ChoiceCard key={r.id} active={role === r.id} onClick={() => setRole(r.id)} testId={`register-role-${r.id}`}>
                  <Briefcase className="w-5 h-5" />
                  <div>
                    <div className="text-sm font-medium">{r.label}</div>
                    <div className="text-[11px] text-white/50">{r.tag}</div>
                  </div>
                </ChoiceCard>
              ))}
            </div>
            {role === 'other' && (
              <Field label="Tell us more">
                <Input value={roleOther} onChange={setRoleOther} testId="register-role-other" />
              </Field>
            )}
          </Card>
        )}

        {step === 3 && (
          <Card>
            <h2 className="text-2xl font-serif text-white mb-2">Services & Products of interest</h2>
            <p className="text-white/60 text-sm mb-5">
              Pick from the list, or add new — our AI will normalise and route them automatically.
            </p>
            <CatalogPicker
              kind="service" catalog={serviceCatalog} value={services}
              onAdd={(n, isNew, d) => addService(n, isNew, d)}
              onRemove={(name) => setServices(services.filter(s => s.name !== name))}
              testIdPrefix="register-services"
            />
            <div className="my-6 h-px bg-white/10" />
            <CatalogPicker
              kind="product" catalog={productCatalog} value={products}
              onAdd={(n, isNew, d) => addProduct(n, isNew, d)}
              onRemove={(name) => setProducts(products.filter(p => p.name !== name))}
              testIdPrefix="register-products"
            />
          </Card>
        )}

        {step === 4 && (
          <Card>
            <h2 className="text-2xl font-serif text-white mb-2">Intent & proposal</h2>
            <p className="text-white/60 text-sm mb-5">Optional — tell us how you would like to collaborate.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Partnership type">
                <Input value={partnershipType} onChange={setPartnershipType} testId="register-partnership-type"
                  placeholder="JV / supply / distribution / financing" />
              </Field>
              <Field label="Objective">
                <Input value={objective} onChange={setObjective} testId="register-objective"
                  placeholder="What outcome do you seek?" />
              </Field>
              <Field label="Description" full>
                <textarea
                  value={intentDesc} onChange={(e) => setIntentDesc(e.target.value)}
                  rows={5} data-testid="register-intent-desc"
                  className="w-full bg-black/30 border border-white/15 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#C9A86A] resize-none"
                  placeholder="Anything else our team should know…"
                />
              </Field>
            </div>
          </Card>
        )}

        {step === 5 && (
          <Card>
            <h2 className="text-2xl font-serif text-white mb-2">Review & submit</h2>
            <p className="text-white/60 text-sm mb-5">Please confirm your details before submitting.</p>
            <div className="space-y-3 text-sm">
              <ReviewRow label="Entity"      value={`${entityType.toUpperCase()} · ${legalName}`} />
              <ReviewRow label="Country"     value={country || '—'} />
              <ReviewRow label="Contact"     value={`${contactName} · ${contactEmail}`} />
              <ReviewRow label="Role"        value={role === 'other' ? `Other — ${roleOther}` : role.toUpperCase()} />
              <ReviewRow label="Services"    value={services.map(s => s.name).join(', ') || '—'} />
              <ReviewRow label="Products"    value={products.map(p => p.name).join(', ') || '—'} />
              <ReviewRow label="Intent"      value={partnershipType || objective || '—'} />
            </div>
          </Card>
        )}

        {/* Navigation */}
        {step > 0 && step <= 5 && (
          <div className="mt-6 flex items-center justify-between" data-testid="register-nav">
            <button onClick={() => setStep(step - 1)} disabled={step === 1}
              className="text-sm text-white/60 hover:text-white flex items-center gap-1 disabled:opacity-30"
              data-testid="register-back-btn">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {step < 5 ? (
              <PrimaryButton inline disabled={!canAdvance} onClick={() => setStep(step + 1)} testId="register-next-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </PrimaryButton>
            ) : (
              <PrimaryButton inline loading={submitting} onClick={submit} testId="register-submit-btn">
                <Sparkles className="w-4 h-4 mr-2" /> Submit application
              </PrimaryButton>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
};


/* ============================================================ subcomponents */
const Shell = ({ children }) => (
  <div className="min-h-screen w-full bg-[#0A0A0A] flex flex-col items-center justify-start py-12 px-6 relative"
    style={{ backgroundImage: 'radial-gradient(circle at 20% 0%, rgba(201,168,106,0.10), transparent 50%), radial-gradient(circle at 80% 100%, rgba(201,168,106,0.06), transparent 50%)' }}>

    {/* Back to homepage — top-left, present on every registration step */}
    <div className="absolute top-5 left-5 md:top-7 md:left-8 z-10 flex items-center gap-3" data-testid="register-back-home-wrap">
      <Link to="/" data-testid="register-back-home-btn"
        className="inline-flex items-center gap-2 text-white/60 hover:text-[#C9A86A] text-xs tracking-[0.18em] uppercase transition-colors group">
        <span className="w-7 h-7 rounded-full border border-white/15 group-hover:border-[#C9A86A]/60 flex items-center justify-center transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" />
        </span>
        Back to homepage
      </Link>
    </div>

    {/* Sign-in shortcut — top-right */}
    <div className="absolute top-5 right-5 md:top-7 md:right-8 z-10">
      <Link to="/admin/login" data-testid="register-go-login-btn"
        className="text-xs tracking-[0.18em] uppercase text-white/55 hover:text-[#C9A86A] transition-colors">
        Already have an account? <span className="text-[#C9A86A] underline ml-1">Sign in</span>
      </Link>
    </div>

    <div className="max-w-3xl w-full text-center mb-8 mt-10 md:mt-6">
      <div className="inline-block px-4 py-1.5 rounded-full border border-[#C9A86A]/40 bg-[#C9A86A]/5 mb-3">
        <span className="text-[10px] tracking-[0.32em] uppercase text-[#C9A86A]">Grupo BMP · Registration</span>
      </div>
      <h1 className="font-serif text-white text-4xl md:text-5xl tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
        Build the next chapter with us
      </h1>
      <p className="text-white/55 text-sm mt-2">
        Multi-company holding · UAE-compliant · Operating across MENA &amp; Africa
      </p>
    </div>
    {children}
  </div>
);

const Header = ({ step }) => (
  <div className="mb-6 flex items-center gap-2" data-testid="register-stepper">
    {Array.from({ length: TOTAL_STEPS }, (_, i) => (
      <div key={i} className={`flex-1 h-1 rounded-full transition-colors ${
        i <= step ? 'bg-[#C9A86A]' : 'bg-white/10'
      }`} data-testid={`register-step-${i}`} />
    ))}
  </div>
);

const Card = ({ children }) => (
  <div className="p-6 md:p-8 rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md shadow-2xl">
    {children}
  </div>
);

const Field = ({ label, children, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-white/50 mb-1.5">{label}</label>
    {children}
  </div>
);

const Input = ({ value, onChange, testId, placeholder, type = 'text' }) => (
  <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder} data-testid={testId}
    className="w-full bg-black/30 border border-white/15 text-white px-3.5 py-2.5 rounded-lg text-sm focus:outline-none focus:border-[#C9A86A] transition-colors" />
);

const ChoiceCard = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
      active
        ? 'border-[#C9A86A] bg-[#C9A86A]/10 text-white'
        : 'border-white/10 bg-black/20 text-white/70 hover:border-[#C9A86A]/40 hover:bg-[#C9A86A]/5'
    }`}>
    {children}
  </button>
);

const PrimaryButton = ({ children, onClick, loading, disabled, inline, testId }) => (
  <button onClick={onClick} disabled={loading || disabled} data-testid={testId}
    className={`${inline ? '' : 'w-full mt-2'} bg-[#C9A86A] hover:bg-[#B7945A] disabled:opacity-40 disabled:cursor-not-allowed text-black px-6 py-3 rounded-lg font-semibold flex items-center justify-center transition-colors`}>
    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
  </button>
);

const ReviewRow = ({ label, value }) => (
  <div className="flex items-start gap-4 py-2 border-b border-white/5 last:border-b-0">
    <div className="w-32 flex-shrink-0 text-[11px] uppercase tracking-[0.16em] text-white/40">{label}</div>
    <div className="text-white/85">{value}</div>
  </div>
);

const CatalogPicker = ({ kind, catalog, value, onAdd, onRemove, testIdPrefix }) => {
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  return (
    <div data-testid={testIdPrefix}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/55 mb-2">
        {kind === 'service' ? 'Services of interest' : 'Products of interest'}
      </div>
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={`${testIdPrefix}-chips`}>
          {value.map((v, i) => (
            <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ${
              v.is_new
                ? 'bg-[#C9A86A]/15 text-[#E8D6AA] border border-[#C9A86A]/40'
                : 'bg-white/8 text-white/85 border border-white/10'
            }`}>
              {v.is_new && <Sparkles className="w-3 h-3" />}
              {v.name}
              <button onClick={() => onRemove(v.name)} data-testid={`${testIdPrefix}-remove-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Catalog suggestions */}
      {catalog.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {catalog.slice(0, 12).map((c, i) => {
            const taken = value.some(v => v.name.toLowerCase() === (c.name || '').toLowerCase());
            return (
              <button key={i} type="button" disabled={taken}
                onClick={() => onAdd(c.name, false)}
                data-testid={`${testIdPrefix}-suggest-${i}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  taken
                    ? 'border-white/10 bg-white/5 text-white/30 cursor-not-allowed'
                    : 'border-white/15 bg-black/30 text-white/75 hover:border-[#C9A86A]/40 hover:text-white'
                }`}>
                + {c.name}
              </button>
            );
          })}
        </div>
      )}
      {/* Add new */}
      <div className="flex flex-col md:flex-row gap-2">
        <input value={newName} onChange={(e) => setNewName(e.target.value)}
          placeholder={`Add new ${kind} (e.g. Renewable energy)`}
          data-testid={`${testIdPrefix}-new-name`}
          className="flex-1 bg-black/30 border border-white/15 text-white px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#C9A86A]" />
        <input value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
          placeholder="Optional description"
          data-testid={`${testIdPrefix}-new-desc`}
          className="flex-1 bg-black/30 border border-white/15 text-white px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#C9A86A]" />
        <button type="button"
          onClick={() => { onAdd(newName, true, newDesc); setNewName(''); setNewDesc(''); }}
          data-testid={`${testIdPrefix}-add-btn`}
          disabled={!newName.trim()}
          className="bg-[#C9A86A]/20 hover:bg-[#C9A86A]/30 disabled:opacity-30 text-[#E8D6AA] px-4 py-2 rounded-lg flex items-center justify-center text-sm border border-[#C9A86A]/40">
          <Plus className="w-4 h-4 mr-1" /> Add
        </button>
      </div>
    </div>
  );
};

export default EntityRegistrationPage;
